import React, { createContext, useContext } from "react";
import { Chip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import type { MediaKitMetricsWindow } from "../../api/podcasts";

export const MediaKitMetricsWindowContext = createContext<
  MediaKitMetricsWindow | undefined
>(undefined);

export function useMediaKitMetricsWindow(): MediaKitMetricsWindow | undefined {
  const w = useContext(MediaKitMetricsWindowContext);
  return w?.enabled ? w : undefined;
}

export type MediaKitContentFormat = "longForm" | "shorts";

export function mediaKitMetricsWindowCaptionText(
  window: MediaKitMetricsWindow,
  format: MediaKitContentFormat,
  inProgressCount?: number,
): string {
  const days = format === "shorts" ? window.shortsDays : window.longFormDays;
  const base = i18n.t("mediaKit:metricsAtDay", { days });
  if (!inProgressCount) return base;
  const noun =
    format === "shorts"
      ? inProgressCount === 1
        ? i18n.t("mediaKit:inProgressShortOne")
        : i18n.t("mediaKit:inProgressShortMany")
      : inProgressCount === 1
        ? i18n.t("mediaKit:inProgressEpisodeOne")
        : i18n.t("mediaKit:inProgressEpisodeMany");
  return i18n.t("mediaKit:metricsAtDayProgress", {
    base,
    count: inProgressCount,
    noun,
  });
}

/** Caption under a consumption block when the media kit compares content at day N. */
export const MediaKitMetricsWindowCaption: React.FC<{
  format: MediaKitContentFormat;
  inProgressCount?: number;
}> = ({ format, inProgressCount }) => {
  useTranslation("mediaKit");
  const window = useMediaKitMetricsWindow();
  if (!window) return null;
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      component="p"
      sx={{ mt: 1.5 }}
    >
      {mediaKitMetricsWindowCaptionText(window, format, inProgressCount)}
    </Typography>
  );
};

export const MediaKitInProgressChip: React.FC = () => {
  const { t } = useTranslation("mediaKit");
  return (
  <Chip
    label={t("inProgress")}
    size="small"
    variant="outlined"
    color="warning"
    sx={{ height: 20, fontSize: "0.7rem" }}
  />
  );
};
